import { useContext, useEffect, useState } from 'react';
import { Building2, ChevronRight, MapPin, Package, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../../config/api';
import './TopSellers.css';
import { UserAuthContext } from '../../UserAuthContext';
import { getRecentlyViewedProductIds } from '../../utils/recentlyViewed';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { productRoute, sellerRoute } from '../../utils/namedRoutes';
import { useLocalizedPath } from '../../contexts/LanguageContext';
import { useHomeSections } from './HomeSectionsContext';
import {
  appendLocationPreference,
  fetchHomepageJson,
  homepageAuthHeaders,
  homepageViewerScope,
  HOMEPAGE_CACHE_TTL,
} from '../../utils/homepageApi';

const SCOPES = ['regional', 'global'];
const emptyScope = () => ({ sellers: [], loading: true, error: false });

// Regional and global rankings share one section; a toggle switches between
// them when both have sellers, and the section hides when neither does.
export default function TopSellers() {
  const { user, location, locationReady } = useContext(UserAuthContext);
  const { personalizationAllowed } = useCookieConsent();
  const { setAvailability } = useHomeSections();
  const localizedPath = useLocalizedPath();
  const [scopes, setScopes] = useState({ regional: emptyScope(), global: emptyScope() });
  const [selectedScope, setSelectedScope] = useState(null);
  const navigate = useNavigate();

  const hasLocation = Boolean(location?.country && location.country !== 'Unknown');

  useEffect(() => {
    if (!locationReady) return undefined;
    const controller = new AbortController();
    const recent = personalizationAllowed ? getRecentlyViewedProductIds().join(',') : '';

    SCOPES.forEach((scope) => {
      const update = (patch) => setScopes((previous) => ({ ...previous, [scope]: { ...previous[scope], ...patch } }));
      update(emptyScope());

      if (scope === 'regional' && !hasLocation) {
        update({ loading: false });
        return;
      }

      const endpoint = new URL(apiUrl('/api/top-sellers'));
      endpoint.searchParams.set('scope', scope);
      endpoint.searchParams.set('personalization', personalizationAllowed ? '1' : '0');
      appendLocationPreference(endpoint, location);
      if (recent) endpoint.searchParams.set('recent', recent);

      fetchHomepageJson(endpoint.toString(), {
        headers: homepageAuthHeaders(user),
        scope: homepageViewerScope(user),
        signal: controller.signal,
        ttlMs: HOMEPAGE_CACHE_TTL.sellers,
      })
        .then((result) => update({ sellers: Array.isArray(result) ? result : [], loading: false }))
        .catch((requestError) => {
          if (requestError.name === 'AbortError') return;
          console.error(`Error loading top sellers (${scope}):`, requestError);
          update({ loading: false, error: true });
        });
    });

    return () => controller.abort();
  }, [user, location, locationReady, personalizationAllowed, hasLocation]);

  const availableScopes = SCOPES.filter((scope) => scopes[scope].sellers.length > 0);
  const loading = SCOPES.some((scope) => scopes[scope].loading);

  useEffect(() => {
    setAvailability('topSellers', !loading && availableScopes.length > 0);
  }, [loading, availableScopes.length, setAvailability]);

  if (!loading && availableScopes.length === 0) return null;

  // Prefer the regional ranking when it has sellers.
  const activeScope = availableScopes.includes(selectedScope) ? selectedScope : availableScopes[0];
  const regional = activeScope === 'regional';
  const sellers = activeScope ? scopes[activeScope].sellers : [];

  const locationLabel = [location?.city, location?.region, location?.country]
    .filter((value) => value && value !== 'Unknown')
    .join(', ');

  return (
    <section className="top-sellers-section" id="top-sellers" aria-labelledby="top-sellers-title">
      <div className="top-sellers-container">
        <header className="top-sellers-header">
          <div>
            <h2 id="top-sellers-title">{regional ? 'Top Sellers in Your Region' : 'Top Sellers'}</h2>
            <p>{regional
              ? locationLabel
                ? `Leading sellers near ${locationLabel}, ranked by city, state, and country relevance.`
                : 'Discover leading sellers ranked by city, state, and country relevance.'
              : 'Meet the sellers behind the most purchased products on Eulua.'}</p>
          </div>
          {availableScopes.length > 1 && (
            <div className="top-sellers-toggle" role="group" aria-label="Seller ranking area">
              {availableScopes.map((scope) => (
                <button
                  key={scope}
                  type="button"
                  aria-pressed={scope === activeScope}
                  className={scope === activeScope ? 'is-active' : ''}
                  onClick={() => setSelectedScope(scope)}
                >
                  {scope === 'regional' ? 'Your region' : 'All regions'}
                </button>
              ))}
            </div>
          )}
        </header>

        {sellers.length > 0 ? (
          <div className="top-sellers-grid">
            {sellers.map((seller) => (
              <article className="top-sellers-card" key={seller.id}>
                <div className="top-sellers-card-header">
                  <span className="top-sellers-rank">#{seller.rank}</span>
                  {seller.sponsored && <span className="top-sellers-sponsored"> Sponsored · Paid advertising</span>}
                  <div className="top-sellers-avatar">
                    {seller.image
                      ? <img src={seller.image} alt={`${seller.name} profile`} />
                      : <Building2 size={34} aria-hidden="true" />}
                  </div>
                  <div className="top-sellers-identity">
                    <div>
                      <h3>{seller.name}</h3>
                    </div>
                    <p><MapPin size={14} /> {seller.location}</p>
                    {regional && seller.regionalMatchLabel && (
                      <span className={`top-sellers-match top-sellers-match--${seller.regionalMatchLevel}`}>
                        {seller.regionalMatchLabel}
                      </span>
                    )}
                  </div>
                </div>

                <div className="top-sellers-stats">
                  <div><strong>{seller.unitsSold}</strong><span>Units sold</span></div>
                  <div><strong>{seller.orderCount}</strong><span>Orders</span></div>
                  <div>
                    <strong>{seller.rating == null ? 'New' : seller.rating.toFixed(1)}</strong>
                    <span>{seller.rating == null ? 'Seller' : <><Star size={12} fill="currentColor" /> Rating</>}</span>
                  </div>
                </div>

                <div className="top-sellers-products">
                  <div className="top-sellers-products-title">
                    <span><Package size={15} /> Best-selling products</span>
                  </div>
                  {seller.topProducts.length > 0 ? seller.topProducts.map((product) => (
                    <button
                      type="button"
                      className="top-sellers-product"
                      key={product.id}
                      onClick={() => navigate(localizedPath(productRoute(product)))}
                    >
                      <img src={product.image} alt={product.name} />
                      <span>
                        <strong>{product.name}</strong>
                        <small>{product.brand}</small>
                      </span>
                    </button>
                  )) : <p className="top-sellers-products-empty">No published products are available.</p>}
                </div>

                <button
                  type="button"
                  className="top-sellers-profile-button"
                  onClick={() => navigate(localizedPath(sellerRoute(seller)))}
                >
                  View Seller Profile <ChevronRight size={17} />
                </button>
              </article>
            ))}
          </div>
        ) : (
          <div className="top-sellers-state">Loading top sellers...</div>
        )}
      </div>
    </section>
  );
}
